import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {FONT, SIZE} from '../../../../styles/foundation/typography'
import {NAVY, BORDER, SUCCESS, WARNING, ERROR} from '../../../../styles/foundation/colors'
import {pct as fmtPct} from '../format'

/**
 * Shared coverage bar — built once here rather than separately in DWS and
 * Analytics. Section reconciliation: DWS genuinely needs the 60/30 coverage
 * threshold derivation (`coverageColor`) for its own sorter-coverage figure;
 * Analytics genuinely needs a caller-supplied `tone` (it renders `spread` in
 * RED, which is not a coverage metric and must never take its colour from
 * these thresholds). One component serves both rather than shipping two
 * near-identical bars that could drift apart.
 *
 * Import this from Analytics too — do not redefine a second bar there.
 */

/** Colour carries the same message as the number beside it. Stops at 60 and 30. */
export function coverageColor(value) {
  return value >= 60 ? SUCCESS : value >= 30 ? WARNING : ERROR
}

/**
 * 62px inline coverage bar. `value` is an UNROUNDED percentage — pct() turns
 * anything in (0,1) into 'Under 1%', so a SKU with 4 sorter readings out of
 * 1000 shipments reads honestly instead of as '0%'.
 *
 * `tone` is optional and overrides the derived colour for callers whose
 * value is not itself a coverage percentage (Analytics' `spread`). Omit it
 * and the bar colours itself from `value` via `coverageColor`.
 *
 * A non-zero share always paints at least 3% of the track, so "some
 * coverage" is never visually identical to "none".
 */
export default function CoverageBar({value, tone}) {
  const color = tone || coverageColor(value)
  const width = value > 0 ? Math.max(3, Math.min(100, value)) : 0

  return (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
      <Box sx={{width: 62, height: 6, flex: '0 0 62px', borderRadius: '3px', bgcolor: BORDER, overflow: 'hidden'}}>
        <Box sx={{height: '100%', width: `${width}%`, bgcolor: color, borderRadius: '3px'}} />
      </Box>
      <Typography component='span' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
        {fmtPct(value)}
      </Typography>
    </Box>
  )
}
